import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, FileText, Send, Loader2, Package } from "lucide-react";
import { API } from "@/lib/api";

/** Monday-of-this-week ISO date. */
function nextMondayISO() {
  const d = new Date();
  const dow = d.getDay(); // 0 Sun ... 6 Sat
  const daysUntilMon = ((8 - dow) % 7) || 7; // always upcoming Monday, not today
  d.setDate(d.getDate() + daysUntilMon);
  return d.toISOString().slice(0, 10);
}

function groupByCategory(items) {
  const g = {};
  for (const it of items) { (g[it.category || "Other"] = g[it.category || "Other"] || []).push(it); }
  return g;
}

async function downloadOrder(oid, fmt) {
  // withCredentials cookie is sent so anchor download would work too, but fetch keeps error handling clean.
  const res = await fetch(`${API}/inventory/weekly-orders/${oid}/export?format=${fmt}`, { credentials: "include" });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const nameMatch = cd.match(/filename="?([^";]+)"?/i);
  const filename = nameMatch ? nameMatch[1] : `Sandar_Order.${fmt === "docx" ? "docx" : "pdf"}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function WeeklyOrderTab({ storeIdOverride } = {}) {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(storeIdOverride || (user?.role === "manager" ? user.store_id : ""));
  const [weekOf, setWeekOf] = useState(nextMondayISO());
  const [qtys, setQtys] = useState({}); // { itemName: number }
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(() => {
    api.get("/inventory/weekly-orders").then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  // One-time bootstrap: fetch suppliers, stores, and recent orders.
  // Never re-runs — so changing store / supplier doesn't nuke the user's entered quantities.
  useEffect(() => {
    api.get("/inventory/suppliers").then((r) => {
      setSuppliers(r.data);
      setSupplier((cur) => cur || r.data[0] || "");
    });
    api.get("/stores").then((r) => {
      setStores(r.data);
      setStoreId((cur) => cur || storeIdOverride || (["admin","super_admin"].includes(user?.role) && r.data[0] ? r.data[0].id : cur));
    });
    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the catalog when the supplier changes. Only reset qtys when supplier ACTUALLY changes
  // to a different value — not on the initial mount when supplier is falsy.
  useEffect(() => {
    if (!supplier) return;
    api.get(`/inventory/catalog?supplier=${encodeURIComponent(supplier)}`)
      .then((r) => { setCatalog(r.data.items); setQtys({}); })
      .catch(() => toast.error("Failed to load catalog"));
  }, [supplier]);

  const grouped = useMemo(() => groupByCategory(catalog), [catalog]);
  const filledCount = useMemo(() => Object.values(qtys).filter((v) => Number(v) > 0).length, [qtys]);
  const totalQty = useMemo(() => Object.values(qtys).reduce((s, v) => s + (Number(v) || 0), 0), [qtys]);

  const setQty = (name, v) => setQtys((q) => ({ ...q, [name]: v }));

  const submit = async () => {
    if (!storeId) return toast.error("Pick a store first");
    const items = catalog
      .filter((c) => Number(qtys[c.name]) > 0)
      .map((c) => ({ name: c.name, unit: c.unit, category: c.category, qty: Number(qtys[c.name]) }));
    if (items.length === 0) return toast.error("Enter a quantity for at least one item");
    setSubmitting(true);
    try {
      await api.post("/inventory/weekly-order", { store_id: storeId, supplier, week_of: weekOf, items, notes });
      toast.success(`Order sent to admin · ${items.length} item${items.length > 1 ? "s" : ""}`);
      setQtys({}); setNotes("");
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-8">
      {/* Header controls */}
      <div className="bg-white p-5 rounded-2xl border border-neutral-200/70 grid gap-4 sm:grid-cols-4">
        <div>
          <Label>Supplier</Label>
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger data-testid="weekly-supplier"><SelectValue placeholder="Choose supplier"/></SelectTrigger>
            <SelectContent>{suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Store</Label>
          {["admin","super_admin"].includes(user?.role) ? (
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger data-testid="weekly-store"><SelectValue placeholder="Choose store"/></SelectTrigger>
              <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input readOnly value={stores.find((s) => s.id === storeId)?.name || "—"} data-testid="weekly-store-locked"/>
          )}
        </div>
        <div>
          <Label>Week starting (Monday)</Label>
          <Input type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} data-testid="weekly-week-of"/>
        </div>
        <div className="flex flex-col justify-end">
          <div className="text-xs text-neutral-500 uppercase tracking-wider">Order so far</div>
          <div className="font-display font-bold text-2xl">
            {filledCount} <span className="text-neutral-400 text-base font-normal">items · {totalQty} qty</span>
          </div>
        </div>
      </div>

      {/* Catalog table grouped by category */}
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden" data-testid={`weekly-cat-${cat}`}>
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <Package className="w-4 h-4 text-[hsl(var(--primary))]"/>
            <h3 className="font-display font-semibold">{cat}</h3>
            <span className="text-xs text-neutral-400">({list.length})</span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-neutral-500 bg-neutral-50">
              <tr>
                <th className="p-3 w-1/2">Item</th>
                <th className="p-3">Unit</th>
                <th className="p-3 text-right">Qty for next week</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.map((it) => (
                <tr key={it.name} data-testid={`weekly-item-${it.name}`}>
                  <td className="p-3 font-medium">{it.name}</td>
                  <td className="p-3 text-neutral-500">{it.unit}</td>
                  <td className="p-3 text-right">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="decimal"
                      value={qtys[it.name] ?? ""}
                      onChange={(e) => setQty(it.name, e.target.value)}
                      placeholder="0"
                      className="w-24 h-9 ml-auto text-right"
                      data-testid={`weekly-qty-${it.name}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Notes + submit */}
      <div className="bg-white p-5 rounded-2xl border border-neutral-200/70 space-y-4">
        <div>
          <Label>Notes for admin (optional)</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Delivery notes, substitutions, urgent items…" data-testid="weekly-notes"/>
        </div>
        <Button
          onClick={submit}
          disabled={submitting || filledCount === 0 || !storeId}
          className="w-full h-11 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
          data-testid="weekly-submit"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Sending…</> : <><Send className="w-4 h-4 mr-2"/>Submit weekly order · {filledCount} item{filledCount === 1 ? "" : "s"}</>}
        </Button>
        <p className="text-xs text-neutral-500 text-center">Admin will be emailed a copy the moment you submit.</p>
      </div>

      {/* Recent submissions */}
      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden" data-testid="weekly-recent">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="font-display font-semibold">Recent orders</h3>
          <span className="text-xs text-neutral-400">{recent.length} total</span>
        </div>
        {recent.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">No orders submitted yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-neutral-500 bg-neutral-50">
              <tr>
                <th className="p-3">Week</th>
                <th className="p-3">Store</th>
                <th className="p-3">Supplier</th>
                <th className="p-3">Items</th>
                <th className="p-3">Submitted</th>
                <th className="p-3 text-right">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {recent.map((o) => (
                <tr key={o.id} data-testid={`weekly-order-${o.id}`}>
                  <td className="p-3 font-mono">{o.week_of}</td>
                  <td className="p-3">{o.store_name}</td>
                  <td className="p-3">{o.supplier}</td>
                  <td className="p-3">{(o.items || []).length}</td>
                  <td className="p-3 text-neutral-500 text-xs">
                    <div>{o.submitted_by_name}</div>
                    <div>{new Date(o.created_at).toLocaleString()}</div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadOrder(o.id, "pdf")} className="rounded-lg" data-testid={`weekly-pdf-${o.id}`}>
                        <FileDown className="w-3.5 h-3.5 mr-1"/>PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadOrder(o.id, "docx")} className="rounded-lg" data-testid={`weekly-doc-${o.id}`}>
                        <FileText className="w-3.5 h-3.5 mr-1"/>Word
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
